import * as React from 'react';

import {
  ensureValue,
  bulkDownloadFromUrl,
  downloadFromUrl,
  PhotoPreviewDialog,
} from '@rematter/ui-web';
import { Button } from '@rematter/ui-web';
import { LucideDownload, LucideLoader2 } from 'lucide-react';

export type PhotoAlbumProps = {
  photos?: { id: string; url?: string; name?: string }[];
  title: string;
};

export const PhotoAlbum = ({ photos, title }: PhotoAlbumProps) => {
  const [preparingAlbumDownload, setPreparingAlbumDownload] =
    React.useState(false);
  const [downloadingPhoto, setDownloadingPhoto] = React.useState<
    Map<string, boolean>
  >(new Map());
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);

  const handlePhotoClick = (index: number) => {
    setCurrentPhotoIndex(index);
    setPreviewOpen(true);
  };

  const handlePhotoDownload =
    (photo: { id: string; url?: string; name?: string }) => () => {
      if (!photo.url || !photo.name) return;
      if (downloadingPhoto.get(photo.id)) return;

      setDownloadingPhoto((prev) => {
        const newMap = new Map(prev);
        newMap.set(photo.id, true);
        return newMap;
      });

      downloadFromUrl(photo.url, ensureValue(photo.name)).finally(() =>
        setDownloadingPhoto((prev) => {
          const newMap = new Map(prev);
          newMap.delete(photo.id);
          return newMap;
        }),
      );
    };

  const handleDownloadAll = async () => {
    if (!photos || photos.length === 0) return;

    setPreparingAlbumDownload(true);

    try {
      await bulkDownloadFromUrl({
        files: photos.map((photo) => ({
          url: ensureValue(photo.url),
          fileName: `${ensureValue(photo.name)}.jpg`,
        })),
        fileName: `${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
      });
    } finally {
      setPreparingAlbumDownload(false);
    }
  };

  return (
    <div className="@PhotoAlbum flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-medium text-neutral/60 uppercase">
          {title}
        </div>
        {!!photos?.length && (
          <Button
            variant="outline"
            color="neutral"
            className="border-neutral/20 px-3"
            onClick={handleDownloadAll}
            isDisabled={preparingAlbumDownload}
          >
            {preparingAlbumDownload ? (
              <LucideLoader2 size={16} className="animate-spin" />
            ) : (
              <LucideDownload size={16} />
            )}
          </Button>
        )}
      </div>
      {!photos?.length && (
        <div className="w-full rounded-md bg-neutral/5 p-6 text-center text-sm text-neutral/40">
          No photos registered yet
        </div>
      )}
      {!!photos?.length && (
        <div className="grid grid-cols-4 gap-2.5 gap-y-4">
          {photos.map((photo, index) => (
            <div key={photo.id} className="relative flex flex-col gap-2">
              <img
                src={photo.url}
                alt={photo.name}
                className="aspect-[3/2] w-full cursor-pointer rounded-md bg-neutral/5 object-cover transition-opacity hover:opacity-80"
                onClick={() => handlePhotoClick(index)}
              />
              <div className="text-xs font-medium">{photo.name}</div>
              <div
                className="absolute top-2 right-2 cursor-pointer rounded bg-neutral/40 p-1.5 text-xs text-white transition-colors hover:bg-neutral/60"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePhotoDownload(photo)();
                }}
              >
                {downloadingPhoto.get(photo.id) ? (
                  <LucideLoader2 size={12} className="animate-spin" />
                ) : (
                  <LucideDownload size={12} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <PhotoPreviewDialog
        albumName={title}
        photos={photos}
        currentIndex={currentPhotoIndex}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onNavigate={setCurrentPhotoIndex}
      />
    </div>
  );
};
